/**
 * Pinia Store para estado global do bot
 * Centraliza dados e ações do bot de trading
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export const useBotStore = defineStore('bot', () => {
  // Estado reativo
  const botStatus = ref({
    isRunning: false,
    lastAnalysis: null,
    activeOrders: 0,
    lastUpdate: new Date()
  });

  const bankSnapshot = ref({
    balance: 0,
    initialBalance: 0,
    profit: 0,
    roi: 0,
    totalTrades: 0,
    winningTrades: 0,
    losingTrades: 0,
    winRate: 0
  });

  const trades = ref([]);
  const claims = ref([]);
  const analyses = ref([]);
  const metrics = ref({
    gemini: null,
    polymarket: null,
    overall: null
  });

  const apiConnected = ref(false);
  const wsConnected = ref(false);
  const loading = ref(false);
  const error = ref(null);

  // Computed properties
  const profitColor = computed(() => {
    if (bankSnapshot.value.profit >= 0) return '#10b981';
    return '#ef4444';
  });

  const statusText = computed(() => {
    return botStatus.value.isRunning ? 'Rodando' : 'Parado';
  });

  const statusColor = computed(() => {
    return botStatus.value.isRunning ? '#10b981' : '#6b7280';
  });

  const roiPercentage = computed(() => {
    return `${bankSnapshot.value.roi.toFixed(2)}%`;
  });

  // Ações
  function setBotStatus(newStatus) {
    botStatus.value = { ...botStatus.value, ...newStatus };
  }

  function setBankSnapshot(newSnapshot) {
    bankSnapshot.value = { ...bankSnapshot.value, ...newSnapshot };
  }

  function addTrade(trade) {
    trades.value.unshift(trade);
    // Limitar a 100 trades em memória
    if (trades.value.length > 100) {
      trades.value.pop();
    }
  }

  function updateTrade(tradeId, updates) {
    const trade = trades.value.find(t => t.id === tradeId);
    if (trade) {
      Object.assign(trade, updates);
    }
  }

  function addClaim(claim) {
    claims.value.unshift(claim);
    if (claims.value.length > 50) {
      claims.value.pop();
    }
  }

  function addAnalysis(analysis) {
    analyses.value.unshift(analysis);
    if (analyses.value.length > 50) {
      analyses.value.pop();
    }
  }

  function setMetrics(newMetrics) {
    metrics.value = newMetrics;
  }

  function setApiConnected(connected) {
    apiConnected.value = connected;
  }

  function setWsConnected(connected) {
    wsConnected.value = connected;
  }

  function setLoading(isLoading) {
    loading.value = isLoading;
  }

  function setError(errorMsg) {
    error.value = errorMsg;
  }

  function clearError() {
    error.value = null;
  }

  function resetState() {
    botStatus.value = {
      isRunning: false,
      lastAnalysis: null,
      activeOrders: 0,
      lastUpdate: new Date()
    };
    bankSnapshot.value = {
      balance: 0,
      initialBalance: 0,
      profit: 0,
      roi: 0,
      totalTrades: 0,
      winningTrades: 0,
      losingTrades: 0,
      winRate: 0
    };
    trades.value = [];
    claims.value = [];
    analyses.value = [];
    metrics.value = { gemini: null, polymarket: null, overall: null };
    error.value = null;
  }

  // Métodos para conectar WebSocket e atualizar em tempo real
  function onWebSocketMessage(event) {
    try {
      const { type, data } = JSON.parse(event.data);

      switch (type) {
        case 'status:updated':
          setBotStatus(data);
          break;
        case 'bank:updated':
          setBankSnapshot(data);
          break;
        case 'trade:new':
          addTrade(data);
          break;
        case 'trade:won':
        case 'trade:lost':
        case 'trade:updated':
          updateTrade(data.id, data);
          break;
        case 'claim:new':
          addClaim(data);
          break;
        case 'analysis:new':
          addAnalysis(data);
          break;
        case 'metrics:updated':
          setMetrics(data);
          break;
        case 'error':
          setError(data.message);
          break;
      }
    } catch (err) {
      console.error('Erro ao processar mensagem WebSocket:', err);
    }
  }

  return {
    // State
    botStatus,
    bankSnapshot,
    trades,
    claims,
    analyses,
    metrics,
    apiConnected,
    wsConnected,
    loading,
    error,

    // Computed
    profitColor,
    statusText,
    statusColor,
    roiPercentage,

    // Methods
    setBotStatus,
    setBankSnapshot,
    addTrade,
    updateTrade,
    addClaim,
    addAnalysis,
    setMetrics,
    setApiConnected,
    setWsConnected,
    setLoading,
    setError,
    clearError,
    resetState,
    onWebSocketMessage,
  };
});

export const useUIStore = defineStore('ui', () => {
  // Estado da UI
  const sidebarOpen = ref(true);
  const theme = ref('dark');
  const autoRefresh = ref(true);
  const refreshInterval = ref(5000); // ms
  const selectedTab = ref('dashboard');

  // Ações
  function toggleSidebar() {
    sidebarOpen.value = !sidebarOpen.value;
  }

  function toggleTheme() {
    theme.value = theme.value === 'dark' ? 'light' : 'dark';
  }

  function setAutoRefresh(enabled) {
    autoRefresh.value = enabled;
  }

  function setRefreshInterval(interval) {
    refreshInterval.value = interval;
  }

  function selectTab(tab) {
    selectedTab.value = tab;
  }

  return {
    sidebarOpen,
    theme,
    autoRefresh,
    refreshInterval,
    selectedTab,
    toggleSidebar,
    toggleTheme,
    setAutoRefresh,
    setRefreshInterval,
    selectTab,
  };
});
